import { t } from '@/lib/i18n';
import { translateError, type ApiErrorDetails } from './translate-error';

/**
 * A request failure resolved into something a merchant can read.
 * `errorCode` is the backend's machine-readable code when one was sent,
 * so callers can branch on it instead of matching message text.
 */
export interface RequestError {
  message: string;
  errorCode: string | null;
}

interface GraphQLErrorLike {
  message?: string;
  extensions?: {
    code?: string;
    errorCode?: string;
    errorDetails?: ApiErrorDetails;
  };
}

interface RequestErrorLike {
  name?: string;
  message?: string;
  graphQLErrors?: GraphQLErrorLike[];
  networkError?: unknown;
}

// Older resolvers throw the plain `apiError()` result object, which GraphQL
// reports as `Unexpected error value: { success: false, errorCode: "X", ... }`.
const LEGACY_ERROR_CODE = /errorCode:\s*"([A-Z0-9_]+)"/;

function codedError(errorCode: string, details?: ApiErrorDetails): RequestError {
  return { message: translateError(errorCode, details), errorCode };
}

/**
 * Turn a GraphQL/network failure into a translated message.
 *
 * - Backend error codes (extensions or legacy thrown objects) → `errors.<CODE>`.
 * - Permission denial → `errors.AUTH_FORBIDDEN`.
 * - Network failure / timeout → translated connection messages.
 * - A raw object dump is never shown; other server messages are kept as sent.
 */
export function resolveRequestError(error: unknown): RequestError {
  if (!error) return { message: t('errors.SYSTEM_ERROR'), errorCode: null };

  const err = error as RequestErrorLike;

  if (err.name === 'AbortError') {
    return { message: t('errors.requestTimeout'), errorCode: null };
  }

  const graphQLError = err.graphQLErrors?.[0];
  if (graphQLError) {
    const extensions = graphQLError.extensions ?? {};
    if (extensions.errorCode) {
      return codedError(extensions.errorCode, extensions.errorDetails);
    }
    const message = graphQLError.message ?? '';
    const legacyCode = message.match(LEGACY_ERROR_CODE)?.[1];
    if (legacyCode) return codedError(legacyCode);
    if (extensions.code === 'FORBIDDEN') {
      return { message: t('errors.AUTH_FORBIDDEN'), errorCode: 'AUTH_FORBIDDEN' };
    }
    if (!message || message.startsWith('Unexpected error value')) {
      return { message: t('errors.SYSTEM_ERROR'), errorCode: null };
    }
    return { message, errorCode: extensions.code ?? null };
  }

  if (err.networkError || /fetch/i.test(err.message ?? '')) {
    return { message: t('errors.networkError'), errorCode: null };
  }

  const message = (err.message ?? '').replace(/^\[GraphQL\]\s*/i, '').trim();
  const legacyCode = message.match(LEGACY_ERROR_CODE)?.[1];
  if (legacyCode) return codedError(legacyCode);
  if (!message || message.startsWith('Unexpected error value')) {
    return { message: t('errors.SYSTEM_ERROR'), errorCode: null };
  }
  return { message, errorCode: null };
}
